import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Input,
  VStack,
  Box,
  Text,
  Badge,
  Spinner,
  HStack,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { FiBookOpen, FiFileText } from 'react-icons/fi';
import { API } from '../../utils/apiBase';

export default function GlobalSearchModal({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [devotionals, setDevotionals] = useState([]);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch data only once when modal opens for the first time
  useEffect(() => {
    if (isOpen && devotionals.length === 0 && studies.length === 0) {
      const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
          const [devRes, stuRes] = await Promise.all([
            fetch(`${API}/devotionals?limit=1000`),
            fetch(`${API}/studies?limit=1000`)
          ]);
          
          if (!devRes.ok || !stuRes.ok) throw new Error('Error de conexión con el servidor');
          
          const devData = await devRes.json();
          const stuData = await stuRes.json();

          const normalizedDevs = devData.data || devData.items || devData.devotionals || devData || [];
          const normalizedStu = stuData.data || stuData.items || stuData.studies || stuData || [];

          setDevotionals(normalizedDevs);
          setStudies(normalizedStu);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [isOpen, devotionals.length, studies.length]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    
    const q = query.trim().toLowerCase();
    
    const devResults = devotionals.filter(d => {
      const title = (d.title || '').toLowerCase();
      const summary = (d.summary || '').toLowerCase();
      const verse = (d.verse_text || d.verse?.text || '').toLowerCase();
      return title.includes(q) || summary.includes(q) || verse.includes(q);
    }).map(d => ({ ...d, _type: 'devotional' }));

    const stuResults = studies.filter(s => {
      const title = (s.tema || '').toLowerCase();
      const summary = (s.resumen || '').toLowerCase();
      const ref = (s.reflexion || '').toLowerCase();
      return title.includes(q) || summary.includes(q) || ref.includes(q);
    }).map(s => ({ ...s, _type: 'study' }));

    return [...devResults, ...stuResults].slice(0, 10); // Limit to top 10 results
  }, [query, devotionals, studies]);

  const handleResultClick = (item) => {
    onClose();
    const id = item.id || item.tema;
    if (item._type === 'devotional') {
      window.location.href = `/devocionales?searchId=${encodeURIComponent(id)}`;
    } else {
      window.location.href = `/estudio-biblico?searchId=${encodeURIComponent(id)}`;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg="bg.card" color="text.default" mt={{ base: 4, md: 16 }}>
        <ModalHeader pb={0}>
          <Input 
            placeholder="Buscar devocionales, estudios, versículos..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="lg"
            variant="flushed"
            borderColor="brand.500"
            _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
            autoFocus
          />
        </ModalHeader>
        <ModalBody pb={6} pt={4} maxH="60vh" overflowY="auto">
          {loading && (
            <HStack justify="center" p={4}>
              <Spinner size="md" color="brand.500" />
              <Text>Buscando...</Text>
            </HStack>
          )}

          {error && <Text color="red.500" textAlign="center">{error}</Text>}

          {!loading && query && results.length === 0 && (
            <Text color="text.muted" textAlign="center" py={4}>
              No se encontraron resultados para "{query}"
            </Text>
          )}

          <VStack align="stretch" spacing={2}>
            {results.map((item, i) => (
              <Box 
                key={i} 
                p={3} 
                borderRadius="md" 
                _hover={{ bg: 'whiteAlpha.100', cursor: 'pointer' }}
                onClick={() => handleResultClick(item)}
                borderBottom="1px solid"
                borderColor="border.default"
              >
                <Flex justify="space-between" align="center" mb={1}>
                  <Text fontWeight="bold" noOfLines={1} flex={1}>
                    {item.title || item.tema}
                  </Text>
                  <Badge colorScheme={item._type === 'devotional' ? 'blue' : 'green'} ml={2} display="flex" alignItems="center">
                    <Icon as={item._type === 'devotional' ? FiFileText : FiBookOpen} mr={1} />
                    {item._type === 'devotional' ? 'Devocional' : 'Estudio'}
                  </Badge>
                </Flex>
                <Text fontSize="sm" color="text.muted" noOfLines={2}>
                  {item.summary || item.resumen}
                </Text>
              </Box>
            ))}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
